import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import config from '../../config';
import { formatDateLong } from '../../utils/dateUtils';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  TextField,
  Grid,
  InputAdornment,
  Button,
  Badge,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import NotesIcon from '@mui/icons-material/Notes';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

function MonthYearPicker({ selectedMonth, selectedYear, filterType, onMonthChange, onYearChange, onFilterTypeChange }) {
  const [open, setOpen] = useState(false);
  const [calYear, setCalYear] = useState(parseInt(selectedYear));

  const navigate = (dir) => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    onMonthChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    onFilterTypeChange('month');
  };

  const label = filterType === 'month'
    ? `${MONTH_NAMES[parseInt(selectedMonth.split('-')[1]) - 1]} ${selectedMonth.split('-')[0]}`
    : `Year ${selectedYear}`;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {filterType === 'month' && (
        <IconButton size="small" onClick={() => navigate(-1)}><ChevronLeftIcon /></IconButton>
      )}
      <Button
        variant="outlined"
        size="small"
        startIcon={<CalendarMonthIcon />}
        onClick={() => { setCalYear(filterType === 'year' ? parseInt(selectedYear) : parseInt(selectedMonth)); setOpen(true); }}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {label}
      </Button>
      {filterType === 'month' && (
        <IconButton size="small" onClick={() => navigate(1)}><ChevronRightIcon /></IconButton>
      )}
      <Button size="small" variant={filterType === 'all' ? 'contained' : 'outlined'} onClick={() => onFilterTypeChange('all')} sx={{ whiteSpace: 'nowrap' }}>
        All Time
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <IconButton size="small" onClick={() => setCalYear(y => y - 1)}><ChevronLeftIcon /></IconButton>
            <Typography variant="h6">{calYear}</Typography>
            <IconButton size="small" onClick={() => setCalYear(y => y + 1)}><ChevronRightIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {MONTH_NAMES.map((name, idx) => (
              <Grid item xs={4} key={name}>
                <Button
                  fullWidth size="small"
                  variant={filterType === 'month' && selectedMonth === `${calYear}-${String(idx + 1).padStart(2, '0')}` ? 'contained' : 'outlined'}
                  onClick={() => { onMonthChange(`${calYear}-${String(idx + 1).padStart(2, '0')}`); onFilterTypeChange('month'); setOpen(false); }}
                >
                  {name.slice(0, 3)}
                </Button>
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ mb: 2 }} />
          <Button fullWidth variant={filterType === 'year' && selectedYear === String(calYear) ? 'contained' : 'outlined'}
            onClick={() => { onYearChange(String(calYear)); onFilterTypeChange('year'); setOpen(false); }}>
            Full Year {calYear}
          </Button>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)}>Cancel</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

function AdvertiserOrdersTab({ orders, loading, error, onOrderClick, getTotalNoteCount, handleOpenNotesDialog }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [organizations, setOrganizations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const now = new Date();
  const initMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(initMonth);
  const [selectedYear, setSelectedYear] = useState(String(now.getFullYear()));
  const [dateFilterType, setDateFilterType] = useState('all');
  
  // Fetch organizations to get proper business names
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await axios.get(`${config.apiUrl}/api/organizations`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        // The API returns { success: true, data: [...], pagination: {...} }
        if (response.data && response.data.success && Array.isArray(response.data.data)) {
          setOrganizations(response.data.data);
        } else if (Array.isArray(response.data)) {
          setOrganizations(response.data);
        } else {
          console.warn('Unexpected organizations response format:', response.data);
          setOrganizations([]);
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
        setOrganizations([]);
      }
    };
    fetchOrganizations();
  }, []);
  
  
  // Extract advertiser name from order, prioritizing organization name
  const getAdvertiserName = (order) => {
    // First check if we have organizationData directly from the API
    if (order.organizationData && order.organizationData.name) {
      return order.organizationData.name;
    }
    
    // If organization ID is available, get name from organizations list
    const organizationId = order.organization || order.organization_id || order.organizationId;
    
    if (organizationId && organizations.length > 0) {
      const org = organizations.find(org => org.id === organizationId);
      if (org) {
        return org.name;
      }
    }
    
    // Last resort fallbacks
    return order.advertiser || 'Unknown Advertiser';
  };

  // Get the order ID (which is actually stored as "organization" in the backend)
  const getOrderId = (order) => {
    // First try the actual order ID
    if (order.id) {
      return order.id;
    }
    // Fall back to organization ID if needed
    return order.organization || order.organization_id || order.organizationId || 'N/A';
  };

  // Extract ad type
  const getAdType = (order) => {
    // If we have audiences with adType values, use those
    if (order.audiences && Array.isArray(order.audiences) && order.audiences.length > 0) {
      // Get unique ad types from all audiences
      const adTypes = [...new Set(order.audiences
        .filter(audience => audience.adType || audience.ad_type)
        .map(audience => audience.adType || audience.ad_type))];
      
      if (adTypes.length > 0) {
        return adTypes.join(', ');
      }
    }
    
    // Fall back to product type if no audience ad types
    return order.productType || order.product_type || 'N/A';
  };

  // Calculate total value from audiences
  const getTotalValue = (order) => {
    let total = 0;
    
    // Try to get from audiences first
    if (order.audiences && Array.isArray(order.audiences)) {
      total = order.audiences.reduce((sum, audience) => {
        const price = parseFloat(audience.price || 0);
        const discount = parseFloat(audience.discount || 0);
        // Calculate subtotal accounting for discount
        const subtotal = Math.max(0, price - discount);
        return sum + subtotal;
      }, 0);
      
      if (total > 0) {
        return total.toFixed(2);
      }
    }
    
    // Fall back to direct value field or totalPrice
    return parseFloat(order.totalPrice || order.value || 0).toFixed(2);
  };

  // Get audience count
  const getAudienceCount = (order) => {
    if (order.audiences && Array.isArray(order.audiences)) {
      return order.audiences.length;
    }
    return 0;
  };

  // Format the status text for display
  const formatStatus = (status) => {
    if (!status) return 'Pending';
    
    // Convert to title case (first letter of each word capitalized)
    return status
      .replace(/_/g, ' ')  // Replace underscores with spaces
      .replace(/-/g, ' ')  // Replace hyphens with spaces
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Get color for status chip
  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return 'success';
      case 'Proofing':
        return 'info';
      case 'In Design':
        return 'warning';
      case 'Waiting for Artwork':
        return 'secondary';
      case 'Uploaded':
        return 'primary';
      case 'Invoiced':
        return 'success';
      case 'Cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  // Handle order click to view details
  const handleOrderClick = (order) => {
    if (onOrderClick) {
      // Create a copy of the order with consistent properties
      const enrichedOrder = {
        ...order,
        // Ensure orderType is set to 'ad' for all advertiser orders
        orderType: 'ad',
        order_type: 'ad'
      };
      
      // If we have organization data, add it to the order
      if (order.organization && organizations.length > 0) {
        const org = organizations.find(org => org.id === order.organization);
        if (org) {
          // Add organization data to the enriched order
          enrichedOrder.organizationData = org;
        }
      }
      
      // Pass the enriched order to the callback
      onOrderClick(enrichedOrder);
    }
  };

  // Handle status filter change
  const handleStatusChange = (event) => {
    setStatusFilter(event.target.value);
  };

  // Handle search query change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Filter orders based on status, date, and search query
  const filteredOrders = orders.filter(order => {
    // Filter by status
    if (statusFilter !== 'all' && order.status !== statusFilter) {
      return false;
    }

    // Filter by date
    if (dateFilterType !== 'all') {
      const orderDate = order.createdAt ? new Date(order.createdAt) : null;
      if (!orderDate) return false;
      if (dateFilterType === 'month') {
        const [y, m] = selectedMonth.split('-').map(Number);
        if (orderDate.getFullYear() !== y || orderDate.getMonth() + 1 !== m) return false;
      } else if (dateFilterType === 'year') {
        if (orderDate.getFullYear() !== parseInt(selectedYear)) return false;
      }
    }

    // Filter by search query
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const advertiserName = getAdvertiserName(order).toLowerCase();
    const orderId = getOrderId(order).toString().toLowerCase();
    const adType = getAdType(order).toLowerCase();
    const status = formatStatus(order.status).toLowerCase();
    return (
      advertiserName.includes(query) ||
      orderId.includes(query) ||
      adType.includes(query) ||
      status.includes(query)
    );
  });

  // Get unique statuses for the dropdown
  const statuses = ['all', ...new Set(orders.map(order => order.status || 'pending'))];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography color="error">Error loading orders: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {/* Filters and search row */}
      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
        {/* Month/Year filter */}
        <Grid item xs={12} sm={12} md="auto">
          <MonthYearPicker
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            filterType={dateFilterType}
            onMonthChange={setSelectedMonth}
            onYearChange={setSelectedYear}
            onFilterTypeChange={setDateFilterType}
          />
        </Grid>

        {/* Status filter */}
        <Grid item xs={12} sm={6} md={3} lg={2}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter"
              value={statusFilter}
              onChange={handleStatusChange}
              label="Status"
            >
              {statuses.map(status => (
                <MenuItem key={status} value={status}>
                  {status === 'all' ? 'All Statuses' : formatStatus(status)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {/* Search field */}
        <Grid item xs={12} sm={6} md lg>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search by advertiser, ID, ad type, or status..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      {/* Results count */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredOrders.length} of {orders.length} orders
        </Typography>
      </Box>

      {/* Orders table */}
      <TableContainer component={Paper}>
        <Table aria-label="advertiser orders table">
          <TableHead>
            <TableRow>
              <TableCell>Order Date</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Advertiser</TableCell>
              <TableCell>Ad Type</TableCell>
              <TableCell>Audiences</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">No orders found</TableCell>
              </TableRow>
            ) : (
              filteredOrders.map((order) => (
                <TableRow 
                  key={order.id}
                  onClick={() => handleOrderClick(order)}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                  }}
                >
                                          <TableCell>
                          {order.createdAt ? formatDateLong(order.createdAt) : 'N/A'}
                        </TableCell>
                  <TableCell>
                    {getOrderId(order)}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={getAdvertiserName(order)}>
                      <span>
                        {getAdvertiserName(order).length > 20 
                          ? `${getAdvertiserName(order).substring(0, 20)}...` 
                          : getAdvertiserName(order)}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{getAdType(order)}</TableCell>
                  <TableCell>{getAudienceCount(order)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={formatStatus(order.status || 'Pending')} 
                      size="small" 
                      color={getStatusColor(order.status || 'Pending')}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>${getTotalValue(order)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                        const existingNotes = [];
                        if (order.notes) {
                          existingNotes.push({
                            id: 'general',
                            content: order.notes,
                            createdBy: 'Order Team',
                            createdAt: order.createdAt || new Date().toISOString(),
                            isNew: false,
                            noteType: 'general'
                          });
                        }
                        if (order.designNotes) {
                          existingNotes.push({
                            id: 'design',
                            content: order.designNotes,
                            createdBy: 'Design Team',
                            createdAt: order.createdAt || new Date().toISOString(),
                            isNew: false,
                            noteType: 'design'
                          });
                        }
                        handleOpenNotesDialog('order', order.id.toString(), getAdvertiserName(order), existingNotes);
                      }}
                      sx={{ 
                        minWidth: 'auto',
                        px: 1,
                        py: 0.5,
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        borderRadius: 1,
                        color: (() => {
                          const totalCount = getTotalNoteCount ? getTotalNoteCount(order, 'order') : 0;
                          return totalCount > 0 ? 'primary.main' : 'text.secondary';
                        })(),
                        '&:hover': {
                          bgcolor: 'action.hover',
                        }
                      }}
                    >
                      Notes
                      {(() => {
                        const totalCount = getTotalNoteCount ? getTotalNoteCount(order, 'order') : 0;
                        return totalCount > 0 ? (
                          <Box
                            component="span"
                            sx={{
                              ml: 0.5,
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              px: 0.5,
                              py: 0.1,
                              borderRadius: '50%',
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              minWidth: 18,
                              height: 18,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {totalCount}
                          </Box>
                        ) : null;
                      })()}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

AdvertiserOrdersTab.propTypes = {
  orders: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onOrderClick: PropTypes.func,
  getTotalNoteCount: PropTypes.func,
  handleOpenNotesDialog: PropTypes.func
};

export default AdvertiserOrdersTab; 